import './style.scss';
import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Utils from '../../../shared/utils';
import Autocomplete from '../../common/autocomplete/Autocomplete';
import QueryService from '../../../shared/queryService';
import ConfigService from '../../../shared/configService';
import { IFeature } from '@esri/arcgis-rest-types';
import { Spinner } from 'react-bootstrap';
import IReportMainFeature from '../EditReport/models/IReportMainFeature';
import { IGADM_0, IGADM_1, IReportMain } from '../../../models/IConfig';
import AuthService from '../../../shared/authService';
import { ToastContainer, toast } from 'react-toastify';
import LoadingOverlay from 'react-loading-overlay';

const NewReport = (props: any) => {
  
  const history = useHistory();
  const {t} = useTranslation('reports');

  const gadm0Config: IGADM_0  = ConfigService.getConfig().agol.gadmLayers.gadm_0;
  const gadm1Config: IGADM_1 = ConfigService.getConfig().agol.gadmLayers.gadm_1;
  const reportMainConfig: IReportMain = ConfigService.getConfig().agol.operationalLayers.reportMain;

  /**
   * Data state
   */
  interface IDataState {
    countries?: any[];
    states?: any;
  }
  const [dataState, setCountriesAndStates] = useState<IDataState>({});

  /**
   * Component state
   */
  const [state, setState] = useState({
    selectedYear: new Date().getFullYear(),
    selectedCountry: '',
    selectedState: '',
    reportAlreadyExists: false,
    isLoading: false
  });

  /**
   * Effect
   */
   useEffect(() => {
    getGADMFeatures();
  }, []);  

  /**
   * Get GADM features
   */
  const getGADMFeatures = async () => {
    try {
      const countries: IFeature[] = await QueryService.queryGADMFeatures(ConfigService.getConfig().agol.gadmLayers.gadm_0.index, `${ConfigService.getConfig().agol.gadmLayers.gadm_0.fields.name0} ASC`);
      
      setCountriesAndStates({
        countries: countries.map((feat: IFeature) => feat.attributes)
      });
      
    }
    catch(err: any) {
      console.error(err);
    }
  };

  /**
   * Handle country change
   * @param country 
   */
  const onCountryChange = async (selectedCountry: any) => {
    const gid0: string = selectedCountry[gadm0Config.fields.gid0];

    Utils.setStateValue('selectedCountry', selectedCountry, state, setState);

    const states: IFeature[] = await QueryService.queryGADMFeatures(gadm1Config.index, `${gadm1Config.fields.name1} ASC`, gadm1Config.fields.gid0, gid0);
    
    setCountriesAndStates({
      countries: dataState.countries,
      states: states.map((feat: IFeature) => feat.attributes)
    });    
  }

  /**
   * Handle country clear
   */
  const onCountryClear = async () => {
    Utils.setStateValue('selectedCountry', null, state, setState);     
     
    setCountriesAndStates({
      countries: dataState.countries,
      states: []
    });
  }

  /**
   * Handle state change
   * @param selectedState 
   */
   const onStateChange = async (selectedState: any) => {
    Utils.setStateValue('selectedState', selectedState, state, setState);
  }

  /**
   * Handle state clear
   */
   const onStateClear = async () => {
    Utils.setStateValue('selectedState', null, state, setState);     
  }

  /**
   * Close form
   */
  const close = () => {
    props.onClose();
  }

  /**
   * Check report exists
   */
   const checkReportExists = async(): Promise <boolean> => {
    try {
      let exists: boolean = false;

      const fields = ConfigService.getConfig().agol.operationalLayers.reportMain.fields;

      let where: string = `${fields.repYear} = ${state.selectedYear} AND ${fields.gid0} = '${state.selectedCountry[(gadm0Config.fields as any).gid0]}'`;

      if(state.selectedState) {
        where = `${where} AND (${fields.gid1} IS NULL OR ${fields.gid1} = '${state.selectedState[(gadm1Config.fields as any).gid1]}')`;
      }
      
      const features = await QueryService.queryFeatures(Utils.getOpLayerQuery(ConfigService.getConfig().agol.operationalLayers.reportMain.index), where);

      exists = features && features.length > 0;
      
        
      return Promise.resolve(exists);
      
    } catch (err: any) {
      return Promise.resolve(false);
    }
  }

  /**
   * Close form
   */
  const start = async () => {
    // Check if report already exists
    Utils.setStateValue('isLoading', true, state, setState);

    const exists = await checkReportExists();
    if(exists) {      
      Utils.setStateValue('isLoading', false, state, setState);
      toast.error(t('newReport.reportAlreadyExists'));
      /* Utils.setStateValue('reportAlreadyExists', true, state, setState);

      setTimeout(() => {
        Utils.setStateValue('reportAlreadyExists', false, state, setState);
      }, 3000);*/

    } else {
      const fields0: any = gadm0Config.fields;
      const fields1: any = gadm1Config.fields;

      const report: IReportMainFeature = {
        attributes: {
          created_user: AuthService.getInstance().user.username,
          created_date: new Date().getTime(),
          GID_0: state.selectedCountry[fields0.gid0],
          NAME_0: state.selectedCountry[fields0.name0],
          GID_1: state.selectedState ? state.selectedState[fields1.gid1] : undefined,
          NAME_1: state.selectedState ? state.selectedState[fields1.name1] : undefined,
          rep_year: state.selectedYear,
          reporting_entity_type: "1",
          submitted: 0,
          published: 0
        }
      };

      try {
        const res: any = await QueryService.applyEdits(reportMainConfig.index, [report], []);
        
        if(res && res.adds.length) {
          const objectId: number = res.adds[0].objectId;

          history.push(`${ConfigService.getConfig().routes?.submissionTool.editReport}?id=${objectId}`);
        }

        Utils.setStateValue('isLoading', false, state, setState);
        toast.success(t('newReport.saveReportSuccess'));

      } catch (err: any) {
        console.log(err);
        Utils.setStateValue('isLoading', false, state, setState);
        toast.error(t('newReport.saveReportError'));
      }
    }
  }

  return(
      <div className="newReportDiv">
        <div className="row shadow-lg p-3 mb-5 bg-white big-border-radius newReportContent" style={{backgroundImage: `url("${process.env.PUBLIC_URL}/assets/background/splash-bg.svg")`}}>
          <div className="closeDiv"><span className="actionIcon closeSpan" onClick={close}>&#x2715;</span></div>

          <div className="row bigText pageTitle">
            <span>{t('newReport.title')}</span> 
          </div>

          <div className="row">    
            <span className="titleSpan mediumText">{t('newReport.reportingYear')}</span>        
            <input className="mediumText yearInput" type="number" placeholder="Insert year" required value={state.selectedYear} onChange={(e) => {Utils.setStateValue('selectedYear', e.target.value, state, setState)}}/>
          </div>

          <div className="row">    
            <span className="titleSpan mediumText">{t('newReport.reportingCountry')}</span>        
            {dataState.countries && dataState.countries.length ? 
            
            <Autocomplete
              className="mediumText"
              id="newReportCountries"
              labelKey={ConfigService.getConfig().agol?.gadmLayers.gadm_0.fields.name0}
              placeholder={t('newReport.selectCountry')}
              data={dataState.countries}
              onChange={onCountryChange}
              onClear={onCountryClear}/> :

            <Spinner animation="grow" variant="dark" />}

          </div>

          {state.selectedCountry &&
            <div>
              <div className="row">
              <span className="mediumText">{t('newReport.stateText')}</span>  
            </div>

            <div className="row">    
              <span className="titleSpan mediumText">{t('newReport.reportingState')}</span>        
              {dataState.states && dataState.states.length ? 
              
              <Autocomplete
                className="mediumText"
                id="newReportStates"
                labelKey={ConfigService.getConfig().agol?.gadmLayers.gadm_1.fields.name1}
                placeholder={t('newReport.selectState')}
                data={dataState.states}
                onChange={onStateChange}
                onClear={onStateClear}/>:
              
              <Spinner animation="grow" variant="dark" />}
            </div>
          </div>
          }

          <div className="row commonText actionsDiv">
              <button className="startBtn primary" onClick={start}>
                  <span>{t('start')}</span> 
              </button>
          </div>
        </div>

         <LoadingOverlay
            active={state.isLoading}
            spinner
            text= {state.isLoading ? t('newReport.savingReport') : ''}>
          </LoadingOverlay>

          <ToastContainer 
            position="bottom-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover/>
      </div>
    );
};

export default NewReport;

